import logger from '../../utils/logger-rum';
import React, {
	useCallback, useEffect, useMemo, useRef, useState, useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import { VideoStream } from '../types/videos';
import { Invitation, Profile } from '../../stores/reducers/invitations';
import { ConnectedUser } from '../../connection/socket';
import blackImage from '../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import { DRAG_TYPE, DropCallback } from '../../stores/reducers/user-interface';
import useShots from '../../contexts/shots/provider';
import EmptyScreenShare from './empty-screen-share';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { SOCKET_MESSAGE_TYPES } from '../../types/constants';
import { SocketContext } from '../../contexts/socket';

interface Props {
	mediaId: string | number;
	feedPosition: number;
}

export default function FeedScreenShare(props: Props) {
	const { feedPosition, mediaId: email } = props;
	const isEmbed = useSelector((s: GS) => s.SessionReducer.embed);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting_id);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedPresenterVideos = useSelector(
		(state: GS) => state.VideoReducer.connectedPresenterVideos,
	);
	const connectedUsers = useSelector(
		(state: GS) => state.SessionReducer.connectedUsers,
	);
	
	const { socket } = useContext(SocketContext);
	
	const [pollingAttempts, setPollingAttempts] = useState(0);
	const [foundVideo, setFoundVideo] = useState<VideoStream | undefined>(undefined);
	const [hasSentNoScreenShareAlert, setHasSentNoScreenShareAlert] = useState(false);
	
	const getScreenShareFromEmail = useCallback((emails: string) => {
		const cUser = connectedUsers.find(
			(connectedUser: ConnectedUser) => connectedUser.email === emails,
		);

		if (!cUser?.screenShareWebrtcId) {
			if (isEmbed && !hasSentNoScreenShareAlert) {	
				logger.error('no screen share webrtc id for embed view', cUser, `\nmeetingId: ${meetingId}`);
				
				// Send socket message to alert hosts about missing screen share
				
			}
			return undefined;
		}
		return connectedPresenterVideos.find(
			(vid: VideoStream) => vid.id === cUser.screenShareWebrtcId,
		);
	}, [connectedUsers, connectedPresenterVideos, isEmbed, meetingId, socket, host, hasSentNoScreenShareAlert]);
	
	// Initial video lookup
	const initialVideo = getScreenShareFromEmail(email as string);
	
	// Polling effect for screen share video
	useEffect(() => {
		if (initialVideo || foundVideo) {
			// Video found, stop polling and reset alert flag
			setPollingAttempts(0);
			setHasSentNoScreenShareAlert(false);
			return;
		}
		
		const maxAttempts = 10; // Poll for up to 10 attempts
		const pollInterval = 1000; // 1 second intervals
		
		if (pollingAttempts >= maxAttempts) {
			logger.warn('Max polling attempts reached for screen share video', {
				email,
				meetingId,
				isEmbed,
				attempts: pollingAttempts
			});
			if (socket && embed) {
				socket.sendMessage(SOCKET_MESSAGE_TYPES.GREENROOM_ALERT, {
					message: 'No Screen Share Available',
					description: `Screen share is not available in embed view for ${email}`,
					duration: 5000,
					type: 'warning',
					host: true, // Only show to hosts
				});
				setHasSentNoScreenShareAlert(true);
			}
			// Reset alert flag so it can try again later
			setHasSentNoScreenShareAlert(false);
			return;
		}
		
		const timer = setTimeout(() => {
			const polledVideo = getScreenShareFromEmail(email as string);
			if (polledVideo) {
				setFoundVideo(polledVideo);
				setPollingAttempts(0);
			} else {
				setPollingAttempts(prev => prev + 1);
			}
		}, pollInterval);
		
		return () => clearTimeout(timer);
	}, [initialVideo, foundVideo, pollingAttempts, getScreenShareFromEmail, email, meetingId, isEmbed]);
	
	const video = foundVideo || initialVideo;
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const { idsOfVideosInCanvas } = useShots();
	const videoElRef = useRef<HTMLVideoElement>(null);

	const isOnCanvas = useMemo(
		() => video && idsOfVideosInCanvas.includes(video.id),
		[video, idsOfVideosInCanvas],
	);

	const userFromEmail = useMemo(() => {
		const connectedUser = connectedUsers.find((user: ConnectedUser) => user.email === email);
		if (connectedUser) return connectedUser.gr_user;
		const invite = invitations.find((invitation: Invitation) => invitation.profile.email === email);
		if (invite?.profile.gr_user) return invite.profile.gr_user;
		return 0;
	}, [email, connectedUsers, invitations]);

	useEffect(() => {
		if (videoElRef.current && video && video?.srcObject !== videoElRef.current.srcObject) {
			videoElRef.current.srcObject = video.srcObject;
			videoElRef.current.volume = 0;
			if (!video.srcObject.active) return;
			videoElRef.current.play().then(() => {
				if (videoElRef.current?.paused) {
					videoElRef.current.play().catch((e) => {
						logger.error('video el play request error', {
							location: 'feed-screenshare second',
						}, e);
					});
				}
			}).catch((e) => {
				logger.error('video el play request error', {
					location: 'feed-screenshare first',
				}, e);
			});
			cleanupVideoStreamElement(videoElRef.current);
		}
	}, [video?.srcObject]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	return (
		video && video?.srcObject.active
			? <>
					<video
						key={video.srcObject.id + feedPosition}
						muted={true}
						autoPlay={true}
						poster={blackImage}
						playsInline
						ref={videoElRef}
					>
					</video>
			</>
			: <>
					<EmptyScreenShare grUserId={userFromEmail} />
				</>
	);
}
